/**
 * Individual form section and field configuration.
 * Keys are used for data binding; labels are for display (Title Case).
 */

export const INDIVIDUAL_SECTION_KEYS = [
  'filingStatus',
  'personalInformation',
  'addressInformation',
  'residencyDetails',
  'healthCoverageInformation',
  'employerDetails',
  'incomeDetails',
  'deductionsAndExpenses',
  'fbar',
  'fatca',
  'declaration',
] as const;

export type IndividualSectionKey = (typeof INDIVIDUAL_SECTION_KEYS)[number];

export interface SectionMeta {
  key: IndividualSectionKey;
  title: string;
  description?: string;
}

export interface FieldMeta {
  key: string;
  label: string;
  placeholder?: string;
  type?: 'text' | 'number' | 'email' | 'select' | 'checkbox' | 'stateSelect' | 'date';
  fullWidth?: boolean;
  /** For select: reference to options source (e.g. enum name) */
  optionsKey?: string;
  /** Field is required per backend schema */
  required?: boolean;
  /** For personalInformation only: show this field only when personType is one of these (e.g. SPOUSE, DEPENDENT) */
  showWhenPersonType?: string[];
  /** For date fields: use 'monthYear' to show MM/yyyy only (e.g. placed in service). Default is full date MM/dd/yyyy. */
  dateFormat?: 'date' | 'monthYear';
}

export const INDIVIDUAL_SECTIONS: SectionMeta[] = [
  { key: 'filingStatus', title: 'Filing Status' },
  {
    key: 'personalInformation',
    title: 'Personal Information',
    description: 'Taxpayer, spouse, and dependents. Provide full name, date of birth, contact details, immigration and visa information, occupation, marital status (taxpayer only), and SSN/ITIN. Relation to taxpayer is required for dependents only.',
  },
  {
    key: 'addressInformation',
    title: 'Address Information',
    description: 'Current mailing address: street, city, state, and ZIP code.',
  },
  {
    key: 'residencyDetails',
    title: 'Residency Details for States',
    description: 'List state(s) resided and period of stay (start and end dates) for the taxpayer and spouse. Add more states when residency spans multiple states.',
  },
  {
    key: 'healthCoverageInformation',
    title: 'Health Coverage Information',
    description: 'Health coverage status and source (self, employer, or marketplace). Provide form type (1095-A, 1095-B, or 1095-C) if applicable.',
  },
  {
    key: 'employerDetails',
    title: 'Employer Details',
    description: 'Employer name, EIN, and W-2 income details (wages, federal tax withheld, Social Security wages, Medicare wages) for taxpayer and spouse.',
  },
  {
    key: 'incomeDetails',
    title: 'Income Details',
    description: 'Interest or dividend (1099-INT, 1099-DIV); income from sale of stocks (1099-B); state/local refund (1099-G); contract-based income (1099-K, 1099-NEC); other income (e.g., gambling W-2G, cryptocurrency).',
  },
  {
    key: 'deductionsAndExpenses',
    title: 'Other Related Expenses',
    description: 'Home mortgage interest (USA/IN); HSA contributions (1099-SA); IRA contributions (1099-R); student loan interest paid; student tuition and fees paid.',
  },
  {
    key: 'fbar',
    title: 'FBAR',
    description: 'Report if you maintained or transferred funds from your personal account to a personal account in another country (foreign bank and financial account reporting).',
  },
  {
    key: 'fatca',
    title: 'FATCA',
    description: 'If aggregate foreign financial assets exceed $50,000, FATCA reporting may be required.',
  },
  { key: 'declaration', title: 'Declaration' },
];

/** Field configs per section (key = section key). Nested keys use dot notation where used. */
export const INDIVIDUAL_FIELDS: Record<IndividualSectionKey, FieldMeta[]> = {
  filingStatus: [{ key: 'filingStatus', label: 'Filing status', type: 'select', optionsKey: 'INDIVIDUAL_FILING_STATUS' }],
  addressInformation: [
    { key: 'currentAddress', label: 'Street address', placeholder: 'Street address', fullWidth: true, required: true },
    { key: 'city', label: 'City', placeholder: 'City', required: true },
    { key: 'state', label: 'State', type: 'stateSelect', required: true },
    { key: 'zip', label: 'ZIP code', placeholder: 'ZIP code', required: true },
  ],
  personalInformation: [
    { key: 'personType', label: 'Person type', type: 'select', optionsKey: 'PERSON_TYPE', required: true },
    { key: 'firstName', label: 'First name', placeholder: 'First name' },
    { key: 'middleName', label: 'Middle name', placeholder: 'Middle name or initial' },
    { key: 'lastName', label: 'Last name', placeholder: 'Last name' },
    { key: 'dateOfBirth', label: 'Date of birth', placeholder: 'MM/DD/YYYY', type: 'date', required: true },
    { key: 'contact.emailId', label: 'Email address', placeholder: 'Email address' },
    { key: 'contact.mobileNumber', label: 'Mobile number', placeholder: 'Mobile number' },
    { key: 'immigrationDetails.visaType', label: 'Visa type', placeholder: 'Visa type', required: true },
    { key: 'immigrationDetails.firstDateOfEntry', label: 'First date of entry (U.S.)', placeholder: 'MM/DD/YYYY', type: 'date', required: true },
    { key: 'occupation', label: 'Occupation', placeholder: 'Occupation', required: true },
    { key: 'maritalStatus', label: 'Marital status', type: 'select', optionsKey: 'MARITAL_STATUS', required: true, showWhenPersonType: ['TAXPAYER'] },
    { key: 'ssn', label: 'SSN or ITIN', placeholder: 'SSN or ITIN', fullWidth: true, required: true },
    { key: 'relationWithTaxPayer', label: 'Relation to taxpayer', type: 'select', optionsKey: 'RELATION_WITH_TAXPAYER', showWhenPersonType: ['DEPENDENT'] },
  ],
  residencyDetails: [
    { key: 'personType', label: 'Person', type: 'select', optionsKey: 'PERSON_TYPE' },
    { key: 'state', label: 'State', type: 'stateSelect' },
    { key: 'startDate', label: 'Period of stay (start)', placeholder: 'MM/DD/YYYY', type: 'date' },
    { key: 'endDate', label: 'Period of stay (end)', placeholder: 'MM/DD/YYYY', type: 'date' },
  ],
  healthCoverageInformation: [
    { key: 'personType', label: 'Person', type: 'select', optionsKey: 'PERSON_TYPE' },
    { key: 'hasHealthCoverage', label: 'Has health coverage', type: 'checkbox' },
    { key: 'coverageSource', label: 'Coverage source', type: 'select', optionsKey: 'COVERAGE_SOURCE' },
    { key: 'formType', label: 'Form type (1095-A, 1095-B, 1095-C)', type: 'select', optionsKey: 'HEALTH_FORM_TYPE' },
  ],
  employerDetails: [
    { key: 'personType', label: 'Person', type: 'select', optionsKey: 'PERSON_TYPE' },
    { key: 'numberOfEmployers', label: 'Number of employers', placeholder: 'Number', type: 'text' },
    { key: 'employerInfo.0.employerName', label: 'Employer name', placeholder: 'Employer name', fullWidth: true },
    { key: 'employerInfo.0.ein', label: 'EIN', placeholder: 'EIN' },
    { key: 'employerInfo.0.w2Income.wages', label: 'Wages', placeholder: 'Wages', type: 'text' },
    { key: 'employerInfo.0.w2Income.federalTaxWithheld', label: 'Federal tax withheld', placeholder: 'Federal tax withheld', type: 'text' },
    { key: 'employerInfo.0.w2Income.socialSecurityWages', label: 'Social Security wages', placeholder: 'Social Security wages', type: 'text' },
    { key: 'employerInfo.0.w2Income.medicareWages', label: 'Medicare wages', placeholder: 'Medicare wages', type: 'text' },
  ],
  incomeDetails: [
    { key: 'personType', label: 'Person', type: 'select', optionsKey: 'PERSON_TYPE' },
    { key: 'didReceiveInterestOrDividend', label: 'Did you receive any interest or dividend? (1099-INT, 1099-DIV)', type: 'checkbox', fullWidth: true },
    { key: 'didReceiveIncomeFromSaleOfStocks', label: 'Did you receive income from sale of stocks? (1099-B)', type: 'checkbox', fullWidth: true },
    { key: 'didReceiveRefundFromStateLocalLastYear', label: 'Did you receive a refund from state or local last year? (1099-G)', type: 'checkbox', fullWidth: true },
    { key: 'worksContractBasedIncome', label: 'Contract-based income (1099-K, 1099-NEC)', type: 'checkbox', fullWidth: true },
    { key: 'anyOtherIncomeGambling', label: 'Any other income (e.g., gambling W-2G, cryptocurrency)', type: 'checkbox', fullWidth: true },
  ],
  deductionsAndExpenses: [
    { key: 'personType', label: 'Person', type: 'select', optionsKey: 'PERSON_TYPE' },
    { key: 'homeMortgageInterestPaid', label: 'Home mortgage interest paid (USA/IN)', type: 'checkbox', fullWidth: true },
    { key: 'contributionsToHSA', label: 'Contributions to HSA (1099-SA)', type: 'checkbox', fullWidth: true },
    { key: 'contributionsToIRA', label: 'Contributions to IRA (1099-R)', type: 'checkbox', fullWidth: true },
    { key: 'studentLoanInterestPaid', label: 'Student loan interest paid', type: 'checkbox', fullWidth: true },
    { key: 'studentTuitionFeePaid', label: 'Student tuition and fees paid', type: 'checkbox', fullWidth: true },
  ],
  fbar: [
    { key: 'hasForeignBankAccount', label: 'Have you maintained or transferred funds from your personal account to another country’s personal account?', type: 'checkbox', fullWidth: true },
    { key: 'maximumAccountValue', label: 'Maximum account value (USD)', placeholder: 'Maximum value', type: 'text' },
    { key: 'country', label: 'Country', placeholder: 'Country' },
  ],
  fatca: [
    { key: 'applicable', label: 'FATCA applicable (assets exceed $50,000)', type: 'checkbox', fullWidth: true },
    { key: 'assetValue', label: 'Aggregate asset value (USD)', placeholder: 'Asset value', type: 'text' },
    { key: 'country', label: 'Country', placeholder: 'Country' },
  ],
  declaration: [
    { key: 'isInformationAccurate', label: 'I certify that the information provided is accurate and complete.', type: 'checkbox', fullWidth: true },
    { key: 'agreedDate', label: 'Date of agreement', placeholder: 'MM/DD/YYYY', type: 'date' },
  ],
};
